import type { Input } from 'antd';
import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatter, InputDuration } from 'components/atoms/input-duration';
import { OutlineError } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';
import { NumberField } from '../fields';
import { EditableProps } from './editable-props';
import { NoValue } from './no-value';

export const DurationInput = styled(InputDuration)`
  &.has-error {
    ${OutlineError};

    &:focus {
      box-shadow: none;
      ${OutlineError};
    }
  }
`;

interface Props extends Partial<EditableProps<number>> {
  field: NumberField;
}

export const DurationInputValue: FC<Props> = ({
  field,
  id,
  editable,
  onChange,
}) => {
  const effortInputPlaceholder = useTranslation(
    'timesheets.placeholders.time_tracked',
  );

  const inputRef = useRef<Input>(null);

  useEffect(() => {
    queueMacrotask(() => {
      inputRef.current?.focus({ cursor: 'all' });
    });
  }, []);

  return !editable ? (
    field.value == null || field.value === 0 ? (
      <NoValue />
    ) : (
      <>{formatter(field.value)}</>
    )
  ) : (
    <DurationInput
      id={id}
      data-cy="ag-grid.duration.editor"
      ref={inputRef}
      placeholder={effortInputPlaceholder}
      value={field.value}
      min={field.metaData?.range?.min}
      max={field.metaData?.range?.max}
      onChange={(latest) => {
        if ((latest ?? 0) !== (field.value ?? 0)) {
          onChange?.(latest === 0 ? undefined : (latest as number));
        }
      }}
    />
  );
};
